import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  Stack,
  Typography,
  useTheme,
  Avatar,
  IconButton,
  Divider,
  CircularProgress,
  Link,
  Alert,
} from "@mui/material";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import { useSessionQuery, useLogout } from "../../auth";
import { ROUTES } from "../../../shared/utils";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";

export const Dashboard: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { data: session } = useSessionQuery();
  const logoutMutation = useLogout();
  const isWhoopConnected = Boolean(
    session?.user?.integrations?.whoop?.connected
  );
  const isFitbitConnected = Boolean(
    session?.user?.integrations?.fitbit?.connected
  );
  const connectedProvider = isWhoopConnected
    ? "Whoop"
    : isFitbitConnected
      ? "Fitbit"
      : null;

  const handleLogout = async () => {
    try {
      await logoutMutation.mutateAsync();
      navigate("/");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const getInitials = () => {
    if (!session?.user) return "U";
    const first = session.user.firstName?.[0] || "";
    const last = session.user.lastName?.[0] || "";
    return `${first}${last}`.toUpperCase() || "U";
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: { xs: 3, md: 6 },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: 0,
          background:
            theme.palette.mode === "light"
              ? `url("/dashboard-light.png") no-repeat center center`
              : `url("/dashboard-dark.png") no-repeat center center`,
          backgroundSize: "cover",
        }}
      />

      <Card
        variant="outlined"
        sx={{
          width: "100%",
          maxWidth: 800,
          zIndex: 1,
          borderRadius: 4,
          p: { xs: 3, md: 4 },
          textAlign: "center",
          borderColor: "divider",
          background: theme.palette.background.paper,
        }}
      >
        <Stack spacing={3}>
          <Stack spacing={1} textAlign="left">
            <Typography variant="h6" component="h2" fontWeight={600}>
              Welcome,{" "}
              <span style={{ textTransform: "capitalize" }}>
                {session?.user?.firstName}
              </span>
              !
            </Typography>

            {connectedProvider ? (
              <Alert severity="success" sx={{ alignSelf: "flex-start", mt: 1 }}>
                Wearable already connected: {connectedProvider}. You're all set
                — data will sync automatically.
              </Alert>
            ) : (
              <Box
                sx={{
                  width: "100%",
                  pt: 2,
                  gap: 2,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Pair your wearable device to unlock your PEAK4 programme.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  color="primary"
                  sx={{ alignSelf: "flex-start", width: "100%" }}
                  onClick={() => navigate(ROUTES.CONNECT_DEVICE)}
                  endIcon={<ArrowForwardRoundedIcon />}
                >
                  Connect my wearable
                </Button>
              </Box>
            )}
          </Stack>
          <Divider />
          <Card
            variant="outlined"
            sx={{
              width: "100%",
              borderRadius: 2,
              p: 2,
              borderColor: "divider",
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              justifyContent="space-between"
              alignItems="center"
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    bgcolor: "primary.main",
                  }}
                >
                  {getInitials()}
                </Avatar>
                <Stack spacing={0} sx={{ flexGrow: 1, textAlign: "left" }}>
                  <Typography variant="subtitle1">
                    {session?.user?.firstName} {session?.user?.lastName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {session?.user?.email}
                  </Typography>
                </Stack>
              </Stack>
              <IconButton
                onClick={handleLogout}
                loading={logoutMutation.isPending}
                disabled={logoutMutation.isPending}
              >
                {logoutMutation.isPending ? (
                  <CircularProgress size={20} color="primary" />
                ) : (
                  <LogoutRoundedIcon color="primary" />
                )}
              </IconButton>
            </Stack>
          </Card>

          <Typography
            variant="caption"
            color="text.secondary"
            textAlign="center"
          >
            <Link
              href="https://peak4.co.uk/policies/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              color="primary"
            >
              Privacy Policy
            </Link>
          </Typography>
        </Stack>
      </Card>
    </Box>
  );
};
